/**
 * Cancel Subscription Modal - Trade Loop v2.0
 *
 * Modal that explains the cancellation process and confirms user intent
 * - Shows access retained until period end
 * - Explains no refund policy
 * - Shows end date
 * - Allows user to confirm or cancel
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AlertTriangle, Calendar, XCircle, Info } from 'lucide-react';

interface CancelSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentPlan: string;
  periodEndDate?: Date;
  isLoading?: boolean;
}

export function CancelSubscriptionModal({
  isOpen,
  onClose,
  onConfirm,
  currentPlan,
  periodEndDate,
  isLoading = false,
}: CancelSubscriptionModalProps) {
  const [isConfirming, setIsConfirming] = useState(false);

  const currentPlanName = currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1);

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  const handleConfirm = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
      onClose();
    } catch (error: any) {
      console.error('Cancel subscription confirmation error:', error);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-600" />
            Cancel Subscription
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to cancel your <strong>{currentPlanName}</strong> subscription?
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Important Info Box */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h4 className="font-semibold text-red-900 text-sm">What happens when you cancel</h4>
              </div>
            </div>

            <ul className="space-y-2 text-sm text-red-800 ml-7">
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>
                  <strong>Access retained:</strong> You'll keep all features of your current plan until the end of your billing period.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>
                  <strong>No refund:</strong> You will not receive a refund for the remaining time on your current billing period.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>
                  <strong>Downgrade to Starter:</strong> After your billing period ends, you'll be downgraded to the free Starter plan.
                </span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-600 font-bold">•</span>
                <span>
                  <strong>Reactivate anytime:</strong> You can reactivate your subscription before the period ends.
                </span>
              </li>
            </ul>
          </div>

          {/* Access Until Date */}
          {periodEndDate && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
              <Calendar className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-semibold text-blue-900 text-sm mb-1">Access Until</h4>
                <p className="text-sm text-blue-700">
                  You'll have access to all {currentPlanName} features until{' '}
                  <strong>{formatDate(periodEndDate)}</strong>
                </p>
              </div>
            </div>
          )}

          {/* Additional Info */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-gray-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm text-gray-700">
                  If you're experiencing issues or have feedback, we'd love to hear from you.{' '}
                  <a href="mailto:support@gotradeloop.com" className="text-blue-600 hover:underline">
                    Contact our support team
                  </a>{' '}
                  before canceling.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isConfirming || isLoading}
          >
            Keep Subscription
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={isConfirming || isLoading}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            {isConfirming || isLoading ? 'Canceling...' : 'Yes, Cancel Subscription'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
